import { Component, OnInit, signal, WritableSignal } from '@angular/core';
import { ButtonPadding, ButtonVariant } from '@app/components/button/button.component';
import { ParticipantService } from '@shared/services/participant/participant.service';
import { PlIcon } from '@app/icon-library/icons';
import { CertificateColor } from '@app/profile/certificatesTab/certificate/certificate.component';
import { EntityStatus, Level, Module, Step, StepCategory } from '@shared/models/menu.model';
import { ApiUrlStep, CourseContentService } from '@app/courseContent/courseContent.service';
import { combineLatest, first, Observable } from 'rxjs';
import {
  Block,
  BlockButton,
  BlockType,
  ButtonCategory,
  ErrorStepData,
  isStepData,
  StepData,
} from '@shared/models/step.model';
import { I18nService } from '@app/i18n';

interface Certificate {
  buttonId: number;
  certificateName: string;
  certificateJsonFileToGeneratePdf: string;
  color: CertificateColor;
  title: string;
  lockedMessage: string | null;
}

interface CertificateData {
  certificateStepFromMenu: CertificateStep;
  stepData?: StepData;
}

interface StepIdToCertificateStepData {
  [stepId: string]: CertificateData;
}

interface CertificateStep extends Step {
  levelCompletionTitle: string;
  certificateColor: CertificateColor;
  levelLockedMessage: string;
}

@Component({
  selector: 'pl-certificates-tab',
  templateUrl: './certificatesTab.component.html',
})
export class CertificatesTabComponent implements OnInit {
  protected readonly ButtonPadding: typeof ButtonPadding = ButtonPadding;
  protected readonly ButtonVariant: typeof ButtonVariant = ButtonVariant;
  protected readonly PlIcon: typeof PlIcon = PlIcon;

  public isLoading: WritableSignal<boolean> = signal(true);
  public certificates: WritableSignal<Certificate[]> = signal([]);

  constructor(
    private i18n: I18nService,
    private participantService: ParticipantService,
    private courseContentService: CourseContentService,
  ) {}

  public ngOnInit(): void {
    const certificateStepsFromMenu: CertificateStep[] = this.getCertificateStepsFromMenu(
      this.participantService.menu()?.menudata[0].children,
    );

    const certificateStepsFullUrl: Observable<StepData | ErrorStepData>[] = certificateStepsFromMenu.map(
      (step: Step): Observable<StepData | ErrorStepData> => {
        return this.courseContentService.getStepData(ApiUrlStep.step, step.fullUrl, { slug: step.fullUrl });
      },
    );

    if (certificateStepsFullUrl.length > 0) {
      combineLatest(certificateStepsFullUrl)
        .pipe(first())
        .subscribe({
          next: (stepsData: (StepData | ErrorStepData)[]) => {
            this.certificates.set(this.computeCertificateFromStepData(certificateStepsFromMenu, stepsData));
            this.isLoading.set(false);
          },
          error: (error): void => {
            console.error('\x1b[41m CertificatesTabComponent error=', error, '\x1b');
          },
        });
    } else {
      this.isLoading.set(false);
    }
  }

  public computeCertificateFromStepData(
    certificateStepsFromMenu: CertificateStep[],
    stepsData: (StepData | ErrorStepData)[],
  ): Certificate[] {
    const stepIdToCertificateStepData: StepIdToCertificateStepData = {};
    certificateStepsFromMenu.forEach((certificateStep: CertificateStep): void => {
      stepIdToCertificateStepData[certificateStep.id] = {
        certificateStepFromMenu: certificateStep,
      };
    });

    stepsData.forEach((stepData: StepData | ErrorStepData): void => {
      if (isStepData(stepData) && stepIdToCertificateStepData[stepData.id]) {
        stepIdToCertificateStepData[stepData.id].stepData = stepData;
      } else {
        //  If the participant has limited access to the program (level 1 only or Level 1 & 2), in that case,
        //  the BE will reply with the blockStep (id=121) instead of the requested step. So step IDs won't match.
        console.log(`Step Ids don't match between the steps from the menu & the step data, ignoring step`);
      }
    });

    const certificates: Certificate[] = [];
    certificateStepsFromMenu.forEach((certificateStepFromMenu: CertificateStep): void => {
      const stepDataMatching: StepData | undefined = stepIdToCertificateStepData[certificateStepFromMenu.id].stepData;
      if (stepDataMatching) {
        const certificateButtons: BlockButton[] = stepDataMatching.blocks.filter(
          (value: Block | null) =>
            value && value.element === BlockType.button && value.data.category === ButtonCategory.certificate,
        ) as BlockButton[];

        certificateButtons.forEach((button: BlockButton): void => {
          if (button.data.href) {
            certificates.push({
              buttonId: button.id,
              certificateName: certificateStepFromMenu.levelCompletionTitle,
              certificateJsonFileToGeneratePdf: button.data.href,
              color: certificateStepFromMenu.certificateColor,
              title: this.i18n.translate(`profile.certificates.level_completion_certificate`, {
                level: certificateStepFromMenu.levelCompletionTitle,
              }),
              lockedMessage:
                stepDataMatching?.status === EntityStatus.locked ? certificateStepFromMenu.levelLockedMessage : null,
            });
          } else {
            console.error(`Missing Href for certificate button:`, button);
            throw new Error(`No href for a certificate button, this is not normal`);
          }
        });
      } else {
        console.log(
          `Ignoring step from Menu ${certificateStepFromMenu.fullUrl} as we're missing stepData for step with certificate certificateStepFromMenu:`,
          certificateStepFromMenu,
          '   stepIdToCertificateStepData=',
          stepIdToCertificateStepData,
        );
        // this might happen in 2 scenarios:
        //  - Back end returns an error for retrieving stepData for a particular step, this is not normal but we want to avoid displaying an error just for the certificate component
        //  - The participant has limited access to the program (level 1 only or Level 1 & 2), in that case, the BE will reply with the blockStep (id=121) instead of the requested step. So step IDs won't match.
      }
    });

    return certificates;
  }

  private getCertificateStepsFromMenu(levels?: Level[]): CertificateStep[] {
    const certificateSteps: CertificateStep[] = [];

    if (levels && levels.length > 0) {
      levels.forEach((level: Level, indexLevel: number): void => {
        level.children.forEach((module: Module): void => {
          module.children.forEach((step: Step): void => {
            if (step.category === StepCategory.certificate) {
              const isCertificateLocatedInLastLevel: boolean = indexLevel === levels.length - 1;
              certificateSteps.push({
                ...step,
                certificateColor: isCertificateLocatedInLastLevel ? CertificateColor.blue : CertificateColor.green,
                levelCompletionTitle: isCertificateLocatedInLastLevel
                  ? this.i18n.translate(`profile.certificates.final_level_completion_title`)
                  : level.title,
                levelLockedMessage: this.i18n.translate(`profile.certificates.level_locked_message`, {
                  level: level.title,
                }),
              });
            }
          });
        });
      });
    }

    return certificateSteps;
  }
}
